import { fromIntToMonetary, fromMonetaryToInt } from "@common/helpers/monetary";

export interface AmountWeightedLine {
    id: string;
    amount: number;
}

export function allocateProportionallyByAmount(
    lines: AmountWeightedLine[],
    headerAmount: number,
): Array<{ id: string; amount: number }> {
    const headerCents = fromMonetaryToInt(headerAmount);
    const totalItemCents = lines.reduce(
        (sum, line) => sum + fromMonetaryToInt(line.amount),
        0,
    );
    const shouldSplitEqually = totalItemCents === 0;

    let allocatedCents = 0;
    const allocations = lines.map((line) => {
        const lineCents = fromMonetaryToInt(line.amount);
        const rawCents = shouldSplitEqually
            ? headerCents / lines.length
            : (headerCents * lineCents) / totalItemCents;
        const flooredCents = Math.floor(rawCents);
        allocatedCents += flooredCents;
        return { id: line.id, cents: flooredCents, amount: line.amount };
    });

    const remainderCents = headerCents - allocatedCents;
    if (remainderCents !== 0 && allocations.length > 0) {
        let largestIndex = 0;
        for (let index = 1; index < allocations.length; index++) {
            if (allocations[index].amount >= allocations[largestIndex].amount) {
                largestIndex = index;
            }
        }
        allocations[largestIndex].cents += remainderCents;
    }

    return allocations.map(({ id, cents }) => ({
        id,
        amount: fromIntToMonetary(cents),
    }));
}

export interface OpenWeightedLine {
    id: string;
    openAmount: number;
}

export function allocateProRataByOpenAmount(
    lines: OpenWeightedLine[],
    amount: number,
): Array<[string, number]> {
    const openCents = lines.map((line) =>
        Math.max(0, fromMonetaryToInt(line.openAmount)),
    );
    const totalOpenCents = openCents.reduce((sum, cents) => sum + cents, 0);
    const amountCents = Math.min(
        Math.max(0, fromMonetaryToInt(amount)),
        totalOpenCents,
    );
    const shares = lines.map((line, index) => ({
        id: line.id,
        cents: 0,
        remainderWeight: 0,
        openCents: openCents[index],
        index,
    }));

    if (amountCents > 0 && totalOpenCents > 0) {
        let allocatedCents = 0;
        for (const share of shares) {
            const product = amountCents * share.openCents;
            share.cents = Math.trunc(product / totalOpenCents);
            share.remainderWeight = product % totalOpenCents;
            allocatedCents += share.cents;
        }

        const ranked = [...shares].sort((left, right) => {
            if (right.remainderWeight !== left.remainderWeight) {
                return right.remainderWeight - left.remainderWeight;
            }
            if (right.openCents !== left.openCents) {
                return right.openCents - left.openCents;
            }
            return left.index - right.index;
        });

        let remainderCents = amountCents - allocatedCents;
        for (const share of ranked) {
            if (remainderCents === 0) {
                break;
            }
            if (share.cents < share.openCents) {
                share.cents += 1;
                remainderCents -= 1;
            }
        }
    }

    return shares.map((share) => [share.id, fromIntToMonetary(share.cents)]);
}
